import { useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { GATES } from "@/data/temple";
import CHAPTERS from "@/data/gateChapters.json";
import MasteryGuide from "@/components/temple/MasteryGuide";
import { Seal } from "@/components/temple/Seal";
import { useAuth } from "@/lib/auth";
import GateUnlock from "@/components/temple/GateUnlock";
import PracticeJournal from "@/components/temple/PracticeJournal";
import { Reveal } from "@/components/temple/Reveal";
import { Sigil } from "@/components/temple/Sigil";
import { slugify } from "@/lib/intro";
import { ease } from "@/lib/scroll";
import { useBase } from "@/lib/appMode";

const IMAGES = ["/images/back.jpg", "/images/silk.jpg", "/images/candle.jpg", "/images/breath.jpg", "/images/couple.jpg", "/images/hands.jpg", "/images/ariel.jpg"];
const pad = (n) => String(n).padStart(2, "0");

const GateHero = ({ g, i }) => {
  const { user } = useAuth();
  const base = useBase();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const numY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  return (
    <section ref={ref} data-testid="gate-hero" className="relative min-h-[92svh] overflow-hidden flex items-end pt-28 pb-16">
      <motion.img key={g.name} style={{ y, scale: 1.15 }} src={IMAGES[i % IMAGES.length]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0706] via-[#0A0706]/70 to-[#0A0706]/30" />
      <motion.span
        style={{ y: numY }}
        className="pointer-events-none absolute right-[-2vw] top-16 font-display italic leading-none text-[clamp(12rem,38vw,34rem)] text-[#D9B47A]/15 select-none"
      >
        {pad(i + 1)}
      </motion.span>
      <div className="relative max-w-[1440px] w-full mx-auto px-5 sm:px-10">
        <Link to={base ? "/app/gates" : "/#gates"} data-testid="gate-back-link" className="inline-flex items-center gap-2 font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#CFC2B6] hover:text-[#D9B47A] transition-colors">
          <ArrowLeft className="w-4 h-4" /> All 13 Gates
        </Link>
        <p className="mt-10 font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">Gate {pad(i + 1)} of 13</p>
        <h1 data-testid="gate-title" className="font-display text-[clamp(3rem,9vw,8.5rem)] leading-[0.92] tracking-[-0.02em] mt-6">
          {g.name.split(" ").map((w, k) => (
            <span key={`${g.name}-${k}`} className="inline-block overflow-hidden pb-[0.08em] mr-[0.25em]">
              <motion.span className={`inline-block ${k % 2 ? "italic text-[#D9B47A]" : ""}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.2, delay: 0.1 + k * 0.12, ease }}>
                {w}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1 }} className="mt-8 max-w-2xl text-base sm:text-xl text-[#E4D8CC] leading-relaxed">
          {g.line}
        </motion.p>
        {user?.initiate ? (
          <div className="mt-10 flex items-center gap-5">
            <Seal size={110} />
            <p className="font-display italic text-xl text-[#D9B47A] max-w-[14rem] leading-snug">This gate is open to you, initiate.</p>
          </div>
        ) : i === 4 ? (
          <p data-testid="gate-free-chip" className="mt-8 inline-flex rounded-full border border-[#C2414F] bg-[#5A0F1A]/40 px-4 py-1.5 font-tt-mono text-[12px] uppercase tracking-[0.25em]">Free sample ritual</p>
        ) : null}
      </div>
    </section>
  );
};

const Matrix = ({ g }) => (
  <div data-testid="gate-matrix" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#D9B47A]/20 border border-[#D9B47A]/20">
    {[["Primary inputs", g.inputs], ["Supporting vessels", g.vessels], ["Primary output", g.output], ["Core quality", g.quality]].map(([k, v]) => (
      <div key={k} className="bg-[#0D0908] p-6 sm:p-8">
        <p className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#D9B47A]">{k}</p>
        <p className="mt-3 text-[15px] text-[#E4D8CC] leading-relaxed">{v}</p>
      </div>
    ))}
  </div>
);

const Teaching = ({ paras }) => (
  <div data-testid="gate-teaching" className="max-w-[720px]">
    {paras.map((p, k) => (
      <Reveal key={k} y={20}>
        <p className={`text-[17px] sm:text-[19px] leading-[1.8] text-[#DCCFC3] mb-7 ${k === 0 ? "first-letter:font-display first-letter:text-[5.2rem] first-letter:leading-[0.8] first-letter:float-left first-letter:mr-3 first-letter:mt-2 first-letter:text-[#D9B47A]" : ""}`}>
          {p}
        </p>
      </Reveal>
    ))}
  </div>
);

const Practice = ({ text }) => (
  <Reveal>
    <div data-testid="gate-practice" className="relative overflow-hidden border border-[#C2414F]/40 bg-[#5A0F1A]/25 p-8 sm:p-14">
      <Sigil className="absolute -right-10 -bottom-10 w-56 h-56 opacity-10" width={1.5} />
      <p className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">The Practice, in brief</p>
      <p className="font-display text-2xl sm:text-4xl leading-[1.35] mt-6 text-[#F2E8DF] max-w-4xl">{text}</p>
      <p className="mt-10 font-display italic text-xl text-[#D9B47A]">With love, Goddess Ariel</p>
    </div>
  </Reveal>
);

const Pager = ({ i }) => {
  const base = useBase();
  const prev = GATES[i - 1];
  const next = GATES[i + 1];
  const card = (g, idx, dir) => (
    <Link
      to={`${base}/gates/${slugify(g.name)}`}
      data-testid={`gate-${dir}-link`}
      className={`group flex-1 border border-[#D9B47A]/20 p-8 hover:border-[#D9B47A]/70 hover:bg-[#120C0A] transition-colors duration-500 ${dir === "next" ? "text-right" : ""}`}
    >
      <span className={`flex items-center gap-2 font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#A8998C] ${dir === "next" ? "justify-end" : ""}`}>
        {dir === "prev" && <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />}
        {dir === "prev" ? "Previous" : "Next"} · Gate {pad(idx + 1)}
        {dir === "next" && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
      </span>
      <span className="block font-display text-3xl sm:text-4xl mt-4">{g.name}</span>
    </Link>
  );
  return (
    <div className="flex flex-col sm:flex-row gap-4">
      {prev ? card(prev, i - 1, "prev") : <div className="flex-1 hidden sm:block" />}
      {next ? card(next, i + 1, "next") : (
        <Link to={base ? "/app/journal" : "/#enter"} data-testid="gate-next-link" className="flex-1 border border-[#C2414F]/50 p-8 text-right hover:bg-[#5A0F1A]/30 transition-colors duration-500">
          <span className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#A8998C]">The path continues</span>
          <span className="block font-display text-3xl sm:text-4xl mt-4">{base ? "Return to your journal" : "Enter the Living Temple"}</span>
        </Link>
      )}
    </div>
  );
};

export default function GatePage() {
  const { slug } = useParams();
  const base = useBase();
  const i = GATES.findIndex((g) => slugify(g.name) === slug);
  if (i < 0) {
    return (
      <main className="min-h-[70vh] flex flex-col items-center justify-center px-5 text-center">
        <p className="font-display text-4xl">This gate does not exist.</p>
        <Link to={base ? "/app/gates" : "/#gates"} className="mt-6 text-[#D9B47A] underline">Return to the 13 Gates</Link>
      </main>
    );
  }
  const g = GATES[i];
  const paras = Array.isArray(CHAPTERS[i]) ? CHAPTERS[i] : [];
  const teaching = paras.slice(0, -1);
  const practice = paras[paras.length - 1] ?? "";

  return (
    <main key={slug} data-testid={`gate-page-${i + 1}`}>
      <GateUnlock i={i} name={g.name} />
      <GateHero g={g} i={i} />
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <Matrix g={g} />
        <div className="grid lg:grid-cols-12 gap-12 py-24 sm:py-32">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">The Teaching</p>
              <p className="mt-4 text-sm text-[#A8998C] leading-relaxed">From Book I — The Tantric Temple: A Path to Orgasmic Enlightenment.</p>
              <ol className="hidden lg:block mt-10 space-y-2">
                {GATES.map((x, k) => (
                  <li key={x.name}>
                    <Link to={`${base}/gates/${slugify(x.name)}`} data-testid={`gate-rail-${k + 1}`} className={`flex gap-3 text-sm transition-colors ${k === i ? "text-[#F2E8DF]" : "text-[#8F8176] hover:text-[#CFC2B6]"}`}>
                      <span className="font-tt-mono text-[11px] pt-0.5 text-[#D9B47A]">{pad(k + 1)}</span>
                      {x.name}
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
          <div className="lg:col-span-9">
            <Teaching paras={teaching} />
          </div>
        </div>
        <Practice text={practice} />
        <div className="mt-24">
          <MasteryGuide i={i} />
          <PracticeJournal i={i} />
        </div>
        <div className="pb-24">
          <Pager i={i} />
        </div>
      </div>
    </main>
  );
}
